import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const PickerWrapper = styled.div`
  position: relative;
  width: 100%;
  isolation: isolate;
`;

const MobileBackdrop = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
  }
`;

const InputField = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 40px 10px 12px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.isValid === false
        ? '#EF4444'
        : props.isValid === true
        ? '#22C55E'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : '#E2E8F0'};
  border-radius: 6px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#1E293B')};
  font-size: 13px;
  font-family: inherit;
  cursor: ${(props) => (props.readOnly ? 'pointer' : 'text')};
  transition: all 0.2s;
  line-height: 1.5;

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
    font-size: 13px;
  }

  &:hover {
    border-color: ${(props) =>
      props.isValid === false
        ? '#DC2626'
        : props.isValid === true
        ? '#16A34A'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : '#CBD5E1'};
  }

  &:focus {
    outline: none;
    border-color: ${(props) =>
      props.isValid === false ? '#EF4444' : '#fa4616'};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.isValid === false
          ? 'rgba(239, 68, 68, 0.1)'
          : 'rgba(250, 70, 22, 0.1)'};
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const InputIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  cursor: pointer;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  transition: color 0.2s;
  pointer-events: all;

  &:hover {
    color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#475569')};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DropdownContainer = styled.div`
  position: fixed;
  ${(props) =>
    props.position === 'above'
      ? `
    bottom: ${props.bottomOffset}px;
  `
      : `
    top: ${props.topOffset}px;
  `}
  left: ${(props) => props.leftOffset}px;
  z-index: 10000;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1E293B' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  width: ${(props) => (props.timeOnly ? '280px' : '340px')};
  max-width: calc(100vw - 32px);

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    left: 16px !important;
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    top: 50px !important;
    bottom: auto !important;
  }
`;

const PickerContent = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
`;

const CalendarSection = styled.div`
  flex: 1;
  padding: 16px;
  border-bottom: ${(props) =>
    props.showTime
      ? `1px solid ${
          props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'
        }`
      : 'none'};
  min-width: 260px;
`;

const TimeSection = styled.div`
  width: 100%;
  padding: 16px;
  background: ${(props) =>
    props.timeOnly
      ? 'transparent'
      : props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : '#FAFBFC'};
  box-sizing: border-box;
`;

const MonthHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const MonthTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
`;

const NavButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : 'transparent'};
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#F1F5F9'};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 4px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  padding: 4px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayButton = styled.button`
  aspect-ratio: 1;
  border: none;
  background: ${(props) =>
    props.selected
      ? '#FA4616'
      : props.today
      ? 'transparent'
      : props.otherMonth
      ? 'transparent'
      : props.disabled
      ? 'transparent'
      : 'transparent'};
  color: ${(props) =>
    props.selected
      ? '#ffffff'
      : props.today
      ? '#FA4616'
      : props.otherMonth
      ? props.theme.name === 'dark'
        ? '#475569'
        : '#CBD5E1'
      : props.disabled
      ? props.theme.name === 'dark'
        ? '#475569'
        : '#CBD5E1'
      : props.theme.name === 'dark'
      ? '#E2E8F0'
      : '#334155'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${(props) => (props.selected || props.today ? '600' : '400')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s;
  position: relative;
  min-height: 32px;

  @media (max-width: 768px) {
    min-height: 36px;
    font-size: 14px;
  }

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.selected
        ? '#E03E0C'
        : props.theme.name === 'dark'
        ? 'rgba(250,70,22,0.1)'
        : 'rgba(250,70,22,0.05)'};
    color: ${(props) => (props.selected ? '#ffffff' : '#FA4616')};
  }

  ${(props) =>
    props.today &&
    !props.selected &&
    `
    &:after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%);
      width: 3px;
      height: 3px;
      background: #FA4616;
      border-radius: 2px;
    }
  `}
`;

const TimePickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TimeSectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const TimeDropdowns = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: center;
`;

const DropdownGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const DropdownLabel = styled.label`
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  font-weight: 500;
`;

const TimeDropdown = styled.select`
  width: ${(props) => props.width || '80px'};
  padding: 8px 28px 8px 10px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 6px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 16px;

  &:hover {
    border-color: ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.2)' : '#CBD5E1'};
  }

  &:focus {
    outline: none;
    border-color: #fa4616;
    box-shadow: 0 0 0 2px rgba(250, 70, 22, 0.1);
  }

  option {
    background: ${(props) =>
      props.theme.name === 'dark' ? '#1E293B' : '#ffffff'};
    color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
    padding: 8px;
  }
`;

const Footer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  position: sticky;
  bottom: 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? '#1E293B' : '#ffffff'};
  z-index: 1;
  border-radius: 0 0 12px 12px;

  @media (max-width: 768px) {
    padding: 10px 12px;
  }
`;

const FooterButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.primary
      ? `
    background: #FA4616;
    color: #ffffff;
    border: none;
    
    &:hover {
      background: #E03E0C;
    }
  `
      : `
    background: transparent;
    color: ${props.theme.name === 'dark' ? '#CBD5E1' : '#64748B'};
    border: 1px solid ${
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.2)' : '#E2E8F0'
    };
    
    &:hover {
      background: ${
        props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F8FAFC'
      };
      border-color: ${
        props.theme.name === 'dark' ? 'rgba(255,255,255,0.3)' : '#CBD5E1'
      };
    }
  `}
`;

const DateTimePicker = ({
  value,
  onChange,
  onConfirm,
  onCancel,
  showTime = true,
  timeOnly = false,
  minDate = null,
  maxDate = null,
  confirmText = 'Select',
  cancelText = 'Cancel',
  defaultTime = '10:00 AM', // Default time when opening picker without a value
}) => {
  // Parse defaultTime prop into hour, minute, period
  const parseDefaultTime = (timeStr) => {
    // Handle 24-hour format (HH:MM)
    const time24Match = timeStr.match(/^(\d{1,2}):(\d{2})$/);
    if (time24Match) {
      const hours = parseInt(time24Match[1]);
      const minute = time24Match[2];
      const hour =
        hours === 0 ? '12' : hours > 12 ? String(hours - 12) : String(hours);
      const period = hours >= 12 ? 'PM' : 'AM';
      return {
        hour: hours === 12 ? '12' : hour,
        minute,
        period,
        hours24: hours,
      };
    }
    // Handle 12-hour format (H:MM AM/PM)
    const time12Match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (time12Match) {
      let hours24 = parseInt(time12Match[1]);
      const period = time12Match[3].toUpperCase();
      if (period === 'PM' && hours24 !== 12) hours24 += 12;
      if (period === 'AM' && hours24 === 12) hours24 = 0;
      return { hour: time12Match[1], minute: time12Match[2], period, hours24 };
    }
    // Fallback
    return { hour: '10', minute: '00', period: 'AM', hours24: 10 };
  };
  const defaultTimeParsed = parseDefaultTime(defaultTime);

  const [selectedDate, setSelectedDate] = useState(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    // Parse string value on initialization
    if (typeof value === 'string') {
      const parts = value.split(/[-/]/);
      if (parts.length === 3) {
        let year, month, day;
        if (parts[0].length === 4) {
          year = parseInt(parts[0]);
          month = parseInt(parts[1]) - 1;
          day = parseInt(parts[2]);
        } else {
          month = parseInt(parts[0]) - 1;
          day = parseInt(parts[1]);
          year = parseInt(parts[2]);
        }
        return new Date(year, month, day, 12, 0, 0, 0);
      }
    }
    return null;
  });
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedHour, setSelectedHour] = useState('12');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [selectedPeriod, setSelectedPeriod] = useState('PM');
  const [currentMonth, setCurrentMonth] = useState(() => {
    // For timeOnly mode, we don't need currentMonth
    if (timeOnly) return new Date();
    // For date modes, use value if it's a valid Date, otherwise use today
    if (value instanceof Date) return value;
    return new Date();
  });
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('below');
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [tempDate, setTempDate] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isValidInput, setIsValidInput] = useState(null); // null = no validation, true/false = valid/invalid
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );

  // Track mobile state for readOnly behavior
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Parse date from text input
  const parseDateInput = (input) => {
    if (!input || input.trim() === '') {
      return null;
    }

    // Remove extra spaces and handle multiple separator types
    const cleaned = input.trim().replace(/\s+/g, '/');
    // eslint-disable-next-line no-useless-escape
    const parts = cleaned.split(/[\/\-\.]/);

    if (parts.length < 2) {
      return null;
    }

    const now = new Date();
    const month = parseInt(parts[0]) - 1; // 0-indexed
    const day = parseInt(parts[1]);
    let year = now.getFullYear();

    if (parts[2]) {
      year = parseInt(parts[2]);
      // Handle 2-digit years: 26 -> 2026, 99 -> 1999
      if (year < 100) {
        year += year > 50 ? 1900 : 2000;
      }
    }

    // Validate month and day ranges
    if (
      isNaN(month) ||
      isNaN(day) ||
      month < 0 ||
      month > 11 ||
      day < 1 ||
      day > 31
    ) {
      return null;
    }

    // Create date at noon to avoid timezone issues
    const date = new Date(year, month, day, 12, 0, 0, 0);
    if (date.getMonth() !== month || date.getDate() !== day) {
      return null;
    }

    return date;
  };

  // Handle text input changes
  const handleInputChange = (e) => {
    const inputVal = e.target.value;
    setInputValue(inputVal);

    // For timeOnly mode, don't try to parse as date
    if (timeOnly) {
      // Just update the display, actual time selection happens via dropdowns
      setIsValidInput(null);
      return;
    }

    // Try to parse the input
    const parsedDate = parseDateInput(inputVal);

    if (inputVal.trim() === '') {
      // Empty input - reset validation
      setIsValidInput(null);
      setTempDate(null);
    } else if (parsedDate) {
      // Valid date parsed
      setIsValidInput(true);
      setTempDate(parsedDate);
      setCurrentMonth(parsedDate); // Update calendar to show this month

      // For date-only pickers, immediately update
      if (!showTime) {
        setSelectedDate(parsedDate);
        if (onChange) {
          // Pass YYYY-MM-DD string for date-only pickers to avoid timezone issues
          const dateString = formatDateToYYYYMMDD(parsedDate);
          onChange(dateString);
        }
      }
    } else {
      // Invalid input
      setIsValidInput(false);
    }
  };

  // Format date for display in input
  const formatDateForInput = (date) => {
    if (!date) return '';
    // Ensure date is a Date object
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    if (showTime && selectedTime) {
      return `${month}/${day}/${year} at ${selectedTime}`;
    }

    return `${month}/${day}/${year}`;
  };

  // Format date to YYYY-MM-DD without timezone conversion
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Update input value when selected date changes
  useEffect(() => {
    // For timeOnly mode, display the selected time
    if (timeOnly) {
      if (selectedTime && !isOpen) {
        setInputValue(selectedTime);
        setIsValidInput(null);
      } else if (!selectedTime && !isOpen) {
        setInputValue('');
        setIsValidInput(null);
      }
      return;
    }

    if (selectedDate && !isOpen) {
      const formatted = formatDateForInput(selectedDate);
      setInputValue(formatted);
      setIsValidInput(null); // Reset validation when not typing
    } else if (!selectedDate && !isOpen) {
      setInputValue('');
      setIsValidInput(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedTime, showTime, timeOnly, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside both the wrapper and the dropdown
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        // For date-time pickers, save tempDate if user explicitly selected a date
        if (showTime && tempDate) {
          setSelectedDate(tempDate);
          if (onChange) onChange(tempDate);
        }
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onChange, showTime, tempDate]);

  // Calculate dropdown position to prevent viewport overflow
  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const dropdownHeight = 400; // Approximate height of the dropdown
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (value) {
      // Handle timeOnly mode - value is a time string like "14:30" or "2:30 PM"
      if (timeOnly && typeof value === 'string') {
        // Try to parse time string
        let hour, minute, period;

        // Check for 24-hour format (HH:MM)
        const time24Match = value.match(/^(\d{1,2}):(\d{2})$/);
        // Check for 12-hour format (H:MM AM/PM)
        const time12Match = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

        if (time24Match) {
          const hours = parseInt(time24Match[1]);
          minute = time24Match[2];
          hour =
            hours === 0
              ? '12'
              : hours > 12
              ? String(hours - 12)
              : String(hours);
          period = hours >= 12 ? 'PM' : 'AM';
          if (hours === 12) hour = '12';
        } else if (time12Match) {
          hour = time12Match[1];
          minute = time12Match[2];
          period = time12Match[3].toUpperCase();
        } else {
          // Default
          hour = '12';
          minute = '00';
          period = 'PM';
        }

        setSelectedHour(hour);
        setSelectedMinute(minute);
        setSelectedPeriod(period);
        setSelectedTime(`${hour}:${minute} ${period}`);
        return;
      }

      let dateValue = value;

      // If value is a string, parse it correctly to avoid timezone issues
      if (typeof value === 'string') {
        // Try to parse YYYY-MM-DD or MM/DD/YYYY format
        const parts = value.split(/[-/]/);
        if (parts.length === 3) {
          let year, month, day;

          // Check if it's YYYY-MM-DD format (year is 4 digits and first)
          if (parts[0].length === 4) {
            year = parseInt(parts[0]);
            month = parseInt(parts[1]) - 1; // 0-indexed
            day = parseInt(parts[2]);
          } else {
            // Assume MM/DD/YYYY format
            month = parseInt(parts[0]) - 1; // 0-indexed
            day = parseInt(parts[1]);
            year = parseInt(parts[2]);
          }

          // Create date at noon to avoid timezone issues
          dateValue = new Date(year, month, day, 12, 0, 0, 0);
        } else {
          // Fallback to Date constructor
          dateValue = new Date(value);
        }
      }

      setSelectedDate(dateValue);
      if (showTime && dateValue instanceof Date) {
        const hours = dateValue.getHours();
        const minutes = dateValue.getMinutes();
        const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const period = hours >= 12 ? 'PM' : 'AM';

        setSelectedHour(String(displayHour));
        setSelectedMinute(String(minutes).padStart(2, '0'));
        setSelectedPeriod(period);

        const timeString = `${displayHour}:${String(minutes).padStart(
          2,
          '0'
        )} ${period}`;
        setSelectedTime(timeString);
      }
    } else {
      setSelectedDate(null);
      setSelectedTime(null);
      setSelectedHour('12');
      setSelectedMinute('00');
      setSelectedPeriod('PM');
    }
  }, [value, showTime, timeOnly]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    // getDay() returns 0 for Sunday, which is our first column
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add previous month's trailing days (Sunday = 0, so we need startingDayOfWeek days)
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthDays - i, 12, 0, 0, 0),
        otherMonth: true,
      });
    }

    // Add current month's days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i, 12, 0, 0, 0),
        otherMonth: false,
      });
    }

    // Add next month's leading days to complete the grid
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i, 12, 0, 0, 0),
        otherMonth: true,
      });
    }

    return days;
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  // Use tempDate when picker is open, selectedDate otherwise
  const displayDate = isOpen ? tempDate : selectedDate;

  const isDisabled = (date) => {
    if (minDate) {
      const compareMinDate = new Date(minDate);
      compareMinDate.setHours(0, 0, 0, 0);
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      if (compareDate < compareMinDate) {
        return true;
      }
    }
    if (maxDate) {
      const compareMaxDate = new Date(maxDate);
      compareMaxDate.setHours(0, 0, 0, 0);
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      if (compareDate > compareMaxDate) {
        return true;
      }
    }
    return false;
  };

  const handleDateSelect = (date) => {
    if (!isDisabled(date)) {
      const newDate = new Date(date);
      if (selectedTime && showTime) {
        const [time, period] = selectedTime.split(' ');
        const [hours, minutes] = time.split(':');
        let hour = parseInt(hours);
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
        newDate.setHours(hour, parseInt(minutes), 0, 0);
      } else if (!showTime) {
        // For date-only pickers, set time to noon to avoid timezone issues
        newDate.setHours(12, 0, 0, 0);
      }
      setTempDate(newDate);

      // For date-only pickers, immediately update the value
      if (!showTime) {
        setSelectedDate(newDate);
        if (onChange) {
          // Pass YYYY-MM-DD string for date-only pickers to avoid timezone issues
          const dateString = formatDateToYYYYMMDD(newDate);
          onChange(dateString);
        }
      }
    }
  };

  const handleHourChange = (hour) => {
    setSelectedHour(hour);
    updateTimeFromDropdowns(hour, selectedMinute, selectedPeriod);
  };

  const handleMinuteChange = (minute) => {
    setSelectedMinute(minute);
    updateTimeFromDropdowns(selectedHour, minute, selectedPeriod);
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    updateTimeFromDropdowns(selectedHour, selectedMinute, period);
  };

  const updateTimeFromDropdowns = (hour, minute, period) => {
    let actualHour = parseInt(hour);
    const actualMinute = parseInt(minute);

    // Convert to 24-hour format
    if (period === 'PM' && actualHour !== 12) {
      actualHour += 12;
    } else if (period === 'AM' && actualHour === 12) {
      actualHour = 0;
    }

    // Update selected time string
    const timeString = `${hour}:${minute} ${period}`;
    setSelectedTime(timeString);

    // For timeOnly mode, immediately call onChange with 24-hour format
    if (timeOnly) {
      const time24 = `${String(actualHour).padStart(2, '0')}:${minute}`;
      if (onChange) onChange(time24);
      return;
    }

    // Update temp date
    const dateToUse = tempDate || new Date();
    const newDate = new Date(dateToUse);
    newDate.setHours(actualHour, actualMinute, 0, 0);
    setTempDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const handleConfirm = () => {
    // For timeOnly mode, just close - onChange was already called when time was selected
    if (timeOnly) {
      // Convert current selection to 24-hour format and call onChange
      let actualHour = parseInt(selectedHour);
      if (selectedPeriod === 'PM' && actualHour !== 12) {
        actualHour += 12;
      } else if (selectedPeriod === 'AM' && actualHour === 12) {
        actualHour = 0;
      }
      const time24 = `${String(actualHour).padStart(2, '0')}:${selectedMinute}`;
      const timeDisplay = `${selectedHour}:${selectedMinute} ${selectedPeriod}`;
      setSelectedTime(timeDisplay);
      setInputValue(timeDisplay);
      if (onChange) onChange(time24);
      if (onConfirm) onConfirm(time24);
      setIsOpen(false);
      return;
    }

    const dateToConfirm = tempDate;
    setSelectedDate(dateToConfirm);
    if (onChange) {
      // For date-only pickers, pass YYYY-MM-DD string
      if (!showTime && dateToConfirm) {
        const dateString = formatDateToYYYYMMDD(dateToConfirm);
        onChange(dateString);
      } else {
        // For date-time pickers, pass Date object
        onChange(dateToConfirm);
      }
    }
    if (onConfirm) {
      onConfirm(dateToConfirm);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    // For date-only pickers, clicking cancel should clear the selection
    if (!showTime) {
      setSelectedDate(null);
      setTempDate(null);
      if (onChange) onChange('');
    } else {
      // For date-time pickers, reset to original value
      setTempDate(selectedDate);
      if (selectedDate) {
        const hours = selectedDate.getHours();
        const minutes = selectedDate.getMinutes();
        const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const period = hours >= 12 ? 'PM' : 'AM';

        setSelectedHour(String(displayHour));
        setSelectedMinute(String(minutes).padStart(2, '0'));
        setSelectedPeriod(period);

        const timeString = `${displayHour}:${String(minutes).padStart(
          2,
          '0'
        )} ${period}`;
        setSelectedTime(timeString);
      }
    }
    if (onCancel) {
      onCancel();
    }
    setIsOpen(false);
  };

  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  // Only calculate days when we need the calendar (not in timeOnly mode)
  const days = !timeOnly && currentMonth ? getDaysInMonth(currentMonth) : [];

  // Calculate position for fixed dropdown
  const [dropdownOffsets, setDropdownOffsets] = useState({
    top: 0,
    left: 0,
    bottom: 0,
  });

  useEffect(() => {
    if (isOpen && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      if (dropdownPosition === 'above') {
        setDropdownOffsets({
          bottom: window.innerHeight - rect.top + 8,
          left: rect.left,
          top: 0,
        });
      } else {
        setDropdownOffsets({
          top: rect.bottom + 8,
          left: rect.left,
          bottom: 0,
        });
      }
    }
  }, [isOpen, dropdownPosition]);

  return (
    <PickerWrapper ref={wrapperRef}>
      <InputWrapper>
        <InputField
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onClick={() => {
            // Also open on click for better UX
            if (!isOpen) {
              if (timeOnly) {
                if (!selectedTime) {
                  setSelectedHour('12');
                  setSelectedMinute('00');
                  setSelectedPeriod('PM');
                  setSelectedTime('12:00 PM');
                }
              }
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            // Open dropdown when focused
            if (!isOpen) {
              if (timeOnly) {
                // For timeOnly, just set default time if not set
                if (!selectedTime) {
                  setSelectedHour('12');
                  setSelectedMinute('00');
                  setSelectedPeriod('PM');
                  setSelectedTime('12:00 PM');
                }
              } else if (!selectedDate) {
                // Don't auto-create tempDate — just show calendar at current month
                const today = new Date();
                setCurrentMonth(today);
                if (showTime && !selectedTime) {
                  setSelectedHour(defaultTimeParsed.hour);
                  setSelectedMinute(defaultTimeParsed.minute);
                  setSelectedPeriod(defaultTimeParsed.period);
                  setSelectedTime(
                    `${defaultTimeParsed.hour}:${defaultTimeParsed.minute} ${defaultTimeParsed.period}`
                  );
                }
              } else {
                setTempDate(selectedDate);
                if (showTime && selectedDate) {
                  const hours = selectedDate.getHours();
                  const minutes = selectedDate.getMinutes();
                  const displayHour =
                    hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                  const period = hours >= 12 ? 'PM' : 'AM';

                  setSelectedHour(String(displayHour));
                  setSelectedMinute(String(minutes).padStart(2, '0'));
                  setSelectedPeriod(period);
                }
              }
              setIsOpen(true);
            }
          }}
          placeholder={
            timeOnly
              ? 'Select time'
              : showTime
              ? 'Select date and time'
              : 'MM/DD/YYYY'
          }
          isValid={isValidInput}
          readOnly={timeOnly || isMobile}
        />
        <InputIcon
          onClick={() => {
            // Toggle dropdown when clicking icon
            if (!isOpen) {
              if (timeOnly) {
                // For timeOnly, just set default time if not set
                if (!selectedTime) {
                  setSelectedHour('12');
                  setSelectedMinute('00');
                  setSelectedPeriod('PM');
                  setSelectedTime('12:00 PM');
                }
              } else if (!selectedDate) {
                // Don't auto-create tempDate — just show calendar at current month
                const today = new Date();
                setCurrentMonth(today);
                if (showTime && !selectedTime) {
                  setSelectedHour(defaultTimeParsed.hour);
                  setSelectedMinute(defaultTimeParsed.minute);
                  setSelectedPeriod(defaultTimeParsed.period);
                  setSelectedTime(
                    `${defaultTimeParsed.hour}:${defaultTimeParsed.minute} ${defaultTimeParsed.period}`
                  );
                }
              } else {
                setTempDate(selectedDate);
                if (showTime && selectedDate) {
                  const hours = selectedDate.getHours();
                  const minutes = selectedDate.getMinutes();
                  const displayHour =
                    hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                  const period = hours >= 12 ? 'PM' : 'AM';

                  setSelectedHour(String(displayHour));
                  setSelectedMinute(String(minutes).padStart(2, '0'));
                  setSelectedPeriod(period);
                }
              }
            }
            setIsOpen(!isOpen);
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          {timeOnly ? (
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" strokeWidth="2" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 6v6l4 2"
              />
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          )}
        </InputIcon>
      </InputWrapper>

      {isOpen &&
        ReactDOM.createPortal(
          <>
            <MobileBackdrop
              isOpen={isOpen}
              onClick={() => {
                // For date-time pickers, save tempDate if user explicitly selected a date
                if (showTime && tempDate) {
                  setSelectedDate(tempDate);
                  if (onChange) onChange(tempDate);
                }
                setIsOpen(false);
              }}
            />
            <DropdownContainer
              ref={dropdownRef}
              isOpen={isOpen}
              timeOnly={timeOnly}
              position={dropdownPosition}
              topOffset={dropdownOffsets.top}
              leftOffset={dropdownOffsets.left}
              bottomOffset={dropdownOffsets.bottom}
            >
              <PickerContent>
                {!timeOnly && (
                  <CalendarSection showTime={showTime}>
                    <MonthHeader>
                      <NavButton
                        onClick={() => navigateMonth(-1)}
                        disabled={
                          minDate &&
                          currentMonth.getMonth() === minDate.getMonth() &&
                          currentMonth.getFullYear() === minDate.getFullYear()
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </NavButton>
                      <MonthTitle>
                        {monthNames[currentMonth.getMonth()]}{' '}
                        {currentMonth.getFullYear()}
                      </MonthTitle>
                      <NavButton onClick={() => navigateMonth(1)}>
                        <svg
                          width="16"
                          height="16"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </NavButton>
                    </MonthHeader>

                    <WeekDays>
                      {weekDays.map((day) => (
                        <WeekDay key={day}>{day}</WeekDay>
                      ))}
                    </WeekDays>

                    <DaysGrid>
                      {days.map((day, index) => (
                        <DayButton
                          key={index}
                          selected={isSameDay(day.date, displayDate)}
                          today={isToday(day.date)}
                          otherMonth={day.otherMonth}
                          disabled={isDisabled(day.date)}
                          onClick={() => handleDateSelect(day.date)}
                        >
                          {day.date.getDate()}
                        </DayButton>
                      ))}
                    </DaysGrid>
                  </CalendarSection>
                )}

                {(showTime || timeOnly) && (
                  <TimeSection timeOnly={timeOnly}>
                    <TimePickerContainer>
                      <TimeSectionLabel>Select Time</TimeSectionLabel>
                      <TimeDropdowns>
                        <DropdownGroup>
                          <DropdownLabel>Hour</DropdownLabel>
                          <TimeDropdown
                            width="75px"
                            value={selectedHour}
                            onChange={(e) => handleHourChange(e.target.value)}
                          >
                            {[...Array(12)].map((_, i) => {
                              const hour = i + 1;
                              return (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              );
                            })}
                          </TimeDropdown>
                        </DropdownGroup>

                        <DropdownGroup>
                          <DropdownLabel>Minute</DropdownLabel>
                          <TimeDropdown
                            width="75px"
                            value={selectedMinute}
                            onChange={(e) => handleMinuteChange(e.target.value)}
                          >
                            {[...Array(60)].map((_, i) => {
                              const minuteStr = String(i).padStart(2, '0');
                              return (
                                <option key={i} value={minuteStr}>
                                  {minuteStr}
                                </option>
                              );
                            })}
                          </TimeDropdown>
                        </DropdownGroup>

                        <DropdownGroup>
                          <DropdownLabel>Period</DropdownLabel>
                          <TimeDropdown
                            width="65px"
                            value={selectedPeriod}
                            onChange={(e) => handlePeriodChange(e.target.value)}
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </TimeDropdown>
                        </DropdownGroup>
                      </TimeDropdowns>
                    </TimePickerContainer>
                  </TimeSection>
                )}
              </PickerContent>

              <Footer>
                {!showTime && (
                  <FooterButton
                    onClick={() => {
                      setSelectedDate(null);
                      setTempDate(null);
                      if (onChange) onChange(null);
                      setIsOpen(false);
                    }}
                  >
                    Clear
                  </FooterButton>
                )}
                {showTime && !timeOnly && (
                  <FooterButton
                    onClick={() => {
                      setSelectedDate(null);
                      setTempDate(null);
                      setSelectedTime(null);
                      if (onChange) onChange(null);
                      setIsOpen(false);
                    }}
                  >
                    Clear
                  </FooterButton>
                )}
                {showTime && onCancel && (
                  <FooterButton onClick={handleCancel}>
                    {cancelText}
                  </FooterButton>
                )}
                {(showTime || tempDate) && (
                  <FooterButton primary onClick={handleConfirm}>
                    {confirmText}
                  </FooterButton>
                )}
              </Footer>
            </DropdownContainer>
          </>,
          document.body
        )}
    </PickerWrapper>
  );
};

DateTimePicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onChange: PropTypes.func,
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  showTime: PropTypes.bool,
  timeOnly: PropTypes.bool,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  defaultTime: PropTypes.string,
};

export default DateTimePicker;
